import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  BarChart3, 
  TrendingUp, 
  DollarSign, 
  Activity, 
  Calendar,
  Target,
  Users,
  TreePine,
  Gavel,
  Award,
  Clock,
  CheckCircle2
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';
import { useCurrency } from '@/hooks/useCurrency';

// Sample data for comprehensive analytics
const monthlyRevenueData = [
  { month: 'Jan', revenue: 245000, credits: 4500, avgPrice: 54.4 },
  { month: 'Feb', revenue: 312000, credits: 5200, avgPrice: 60.0 },
  { month: 'Mar', revenue: 189000, credits: 3800, avgPrice: 49.7 },
  { month: 'Apr', revenue: 420000, credits: 6100, avgPrice: 68.9 },
  { month: 'May', revenue: 368000, credits: 5800, avgPrice: 63.4 },
  { month: 'Jun', revenue: 445000, credits: 6500, avgPrice: 68.5 }
];

const projectTypeDistribution = [
  { name: 'Solar Energy', value: 35, color: '#f59e0b' },
  { name: 'Wind Power', value: 28, color: '#3b82f6' },
  { name: 'Forestry', value: 20, color: '#10b981' },
  { name: 'Methane Capture', value: 12, color: '#8b5cf6' },
  { name: 'Other', value: 5, color: '#6b7280' }
];

const verificationTimelineData = [
  { stage: 'Registration', avgDays: 15, projects: 12 },
  { stage: 'Documentation', avgDays: 25, projects: 8 },
  { stage: 'Field Verification', avgDays: 45, projects: 6 },
  { stage: 'Final Review', avgDays: 12, projects: 4 },
  { stage: 'Issuance', avgDays: 8, projects: 4 }
];

const marketPerformanceData = [
  { quarter: 'Q1 2024', marketPrice: 52, yourPrice: 58, volume: 15200 },
  { quarter: 'Q2 2024', marketPrice: 61, yourPrice: 65, volume: 18900 },
  { quarter: 'Q3 2024', marketPrice: 68, yourPrice: 72, volume: 21500 },
  { quarter: 'Q4 2024', marketPrice: 74, yourPrice: 76, volume: 19800 }
];

const buyerSegmentData = [
  { segment: 'Corporates', percentage: 45, value: 2.1 },
  { segment: 'Financial Institutions', percentage: 25, value: 1.2 },
  { segment: 'Government', percentage: 18, value: 0.85 },
  { segment: 'International', percentage: 12, value: 0.58 }
];

const complianceMetrics = [
  { metric: 'CCTS Compliance', score: 98, trend: 2 },
  { metric: 'VCS Standards', score: 96, trend: 1 },
  { metric: 'Gold Standard', score: 94, trend: 3 },
  { metric: 'Documentation Quality', score: 97, trend: 1 },
  { metric: 'Verification Speed', score: 89, trend: -2 }
];

export const ProjectDeveloperAnalytics = () => {
  const { user } = useAuth();
  const { formatPrice } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState({
    totalProjects: 0,
    totalRevenue: 0,
    creditsIssued: 0,
    averagePrice: 0,
    successRate: 0,
    activeListings: 0
  });

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!user) return;

      try {
        // Fetch project data
        const { data: projects } = await supabase
          .from('carbon_projects')
          .select('*')
          .eq('user_id', user.id);

        // Fetch transaction data
        const { data: transactions } = await supabase
          .from('credit_transactions')
          .select('*')
          .eq('seller_id', user.id);

        // Fetch marketplace listings
        const { data: listings } = await supabase
          .from('marketplace_listings')
          .select('*')
          .eq('developer_id', user.id);

        if (projects) {
          const totalCredits = projects.reduce((sum, p) => sum + Number(p.credits_issued || 0), 0);
          const totalRevenue = transactions?.reduce((sum, t) => sum + Number(t.total_amount || 0), 0) || 0;
          const verifiedProjects = projects.filter(p => p.status === 'verified').length;
          const averagePrice = transactions?.length ? 
            transactions.reduce((sum, t) => sum + Number(t.price_per_credit || 0), 0) / transactions.length : 0;

          setAnalyticsData({
            totalProjects: projects.length,
            totalRevenue,
            creditsIssued: totalCredits,
            averagePrice,
            successRate: projects.length ? (verifiedProjects / projects.length) * 100 : 0,
            activeListings: listings?.length || 0
          });
        }
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [user]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-muted animate-pulse rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Project Developer Analytics</h1>
        <p className="text-muted-foreground">
          Comprehensive insights into your project performance and market position
        </p>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-l-4 border-l-primary">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{formatPrice(analyticsData.totalRevenue)}</div>
            <div className="flex items-center gap-1 text-sm text-green-600">
              <TrendingUp className="w-3 h-3" />
              <span>+12.5% vs last quarter</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-success">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Credits Issued</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">{analyticsData.creditsIssued.toLocaleString()}</div>
            <div className="text-sm text-muted-foreground">tCO₂e</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-warning">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Avg. Sale Price</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-warning">{formatPrice(analyticsData.averagePrice)}</div>
            <div className="text-sm text-muted-foreground">per credit</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-info">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Success Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-info">{analyticsData.successRate.toFixed(1)}%</div>
            <div className="text-sm text-muted-foreground">verification success</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="revenue" className="w-full">
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="revenue">Revenue</TabsTrigger>
          <TabsTrigger value="projects">Projects</TabsTrigger>
          <TabsTrigger value="market">Market</TabsTrigger>
          <TabsTrigger value="buyers">Buyers</TabsTrigger>
          <TabsTrigger value="verification">Verification</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
        </TabsList>

        <TabsContent value="revenue" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Monthly Revenue & Volume Trends</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={monthlyRevenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value, name) => [
                    name === 'revenue' ? formatPrice(Number(value)) : Number(value).toLocaleString(),
                    name === 'revenue' ? 'Revenue' : name === 'credits' ? 'Credits Sold' : 'Avg Price'
                  ]} />
                  <Area type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.3} />
                  <Area type="monotone" dataKey="credits" stroke="hsl(var(--success))" fill="hsl(var(--success))" fillOpacity={0.3} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="projects" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Project Type Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={projectTypeDistribution}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {projectTypeDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Project Pipeline Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                      <span>Draft Projects</span>
                    </div>
                    <Badge variant="outline">3</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      <span>Under Verification</span>
                    </div>
                    <Badge variant="outline">5</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <span>Verified & Active</span>
                    </div>
                    <Badge variant="outline">12</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="market" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Market Performance vs Industry Average</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={marketPerformanceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="quarter" />
                  <YAxis />
                  <Tooltip formatter={(value, name) => [formatPrice(Number(value)), name === 'marketPrice' ? 'Market Avg' : 'Your Price']} />
                  <Bar dataKey="marketPrice" fill="hsl(var(--muted-foreground))" name="Market Average" />
                  <Bar dataKey="yourPrice" fill="hsl(var(--primary))" name="Your Price" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="buyers" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Buyer Segment Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {buyerSegmentData.map((segment, index) => (
                  <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex-1">
                      <div className="font-medium">{segment.segment}</div>
                      <div className="text-sm text-muted-foreground">{formatPrice(segment.value * 1000000)} revenue</div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold">{segment.percentage}%</div>
                      <div className="w-20 h-2 bg-muted rounded-full">
                        <div 
                          className="h-full bg-primary rounded-full" 
                          style={{ width: `${segment.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="verification" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Verification Timeline Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={verificationTimelineData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="stage" />
                  <YAxis />
                  <Tooltip formatter={(value, name) => [
                    `${value} ${name === 'avgDays' ? 'days' : 'projects'}`,
                    name === 'avgDays' ? 'Average Days' : 'Active Projects'
                  ]} />
                  <Line type="monotone" dataKey="avgDays" stroke="hsl(var(--primary))" strokeWidth={3} />
                  <Line type="monotone" dataKey="projects" stroke="hsl(var(--success))" strokeWidth={3} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="compliance" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Compliance & Standards Scorecard</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {complianceMetrics.map((metric, index) => (
                  <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <div className="font-medium">{metric.metric}</div>
                      <div className="flex items-center gap-1 text-sm">
                        {metric.trend > 0 ? (
                          <>
                            <TrendingUp className="w-3 h-3 text-green-500" />
                            <span className="text-green-600">+{metric.trend}% improvement</span>
                          </>
                        ) : metric.trend < 0 ? (
                          <>
                            <TrendingUp className="w-3 h-3 text-red-500 rotate-180" />
                            <span className="text-red-600">{metric.trend}% decline</span>
                          </>
                        ) : (
                          <span className="text-muted-foreground">No change</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold">{metric.score}%</div>
                      <div className="w-20 h-2 bg-muted rounded-full">
                        <div 
                          className={`h-full rounded-full ${
                            metric.score >= 95 ? 'bg-green-500' : 
                            metric.score >= 90 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${metric.score}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Time to Market</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">147 days</div>
                <div className="text-sm text-muted-foreground">avg. registration to sale</div>
                <div className="flex items-center gap-1 text-sm text-green-600 mt-2">
                  <TrendingUp className="w-3 h-3" />
                  <span>15% faster than industry</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Credit Utilization</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">78.5%</div>
                <div className="text-sm text-muted-foreground">of issued credits sold</div>
                <div className="w-full h-2 bg-muted rounded-full mt-3">
                  <div className="h-full bg-primary rounded-full" style={{ width: '78.5%' }}></div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Price Premium</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">+8.2%</div>
                <div className="text-sm text-muted-foreground">above market average</div>
                <Badge className="mt-2 bg-success text-success-foreground">Premium Quality</Badge>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};